import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Package, Pencil, Trash2, Truck, Wrench, ArrowRight,
  Undo2, AlertTriangle, ClipboardCheck, Plus, Leaf, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useInventoryItem, useItemMovements, useDeleteInventoryItem,
} from '@/hooks/useInventory';
import {
  LOCATION_LABELS, MOVEMENT_TYPE_LABELS, VENEER_CUT_LABELS,
  VENEER_FIGURE_LABELS, VENEER_MATCH_LABELS,
  type Location, type MovementType, type StockMovement,
  isLowStock, isOutOfStock,
} from '@/types/inventory';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { KindBadge, KindIcon } from '@/components/inventory/KindBadge';
import { ItemFormModal } from '@/components/inventory/ItemFormModal';
import { RecordMovementModal } from '@/components/inventory/RecordMovementModal';
import { formatCurrency, formatNumber } from '@/lib/utils';

const MOVEMENT_ICONS: Record<MovementType, typeof Truck> = {
  receipt:     Truck,
  consumption: Wrench,
  transfer:    ArrowRight,
  adjustment:  ClipboardCheck,
  return:      Undo2,
  scrap:       AlertTriangle,
  initial:     Package,
};

export function InventoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'inventory', 'write') : false;

  const { data: item, isLoading, error } = useInventoryItem(id);
  const { data: movements } = useItemMovements(id);
  const deleteItem = useDeleteInventoryItem();

  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveType, setMoveType] = useState<MovementType>('receipt');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) return <LoaderScreen />;
  if (error || !item) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load item"
          description={(error as { message?: string })?.message ?? 'Item not found.'}
          action={<Link to="/inventory"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Inventory</Button></Link>}
        />
      </Card>
    );
  }

  const low = isLowStock(item);
  const out = isOutOfStock(item);

  function openMove(type: MovementType) {
    setMoveType(type);
    setMoveOpen(true);
  }

  async function onDelete() {
    if (!item) return;
    try {
      await deleteItem.mutateAsync(item.id);
      toast.success(`Deactivated ${item.sku}`);
      navigate('/inventory');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      <Link to="/inventory" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted transition-colors">
        <ArrowLeft size={12} />
        <span className="kicker">All Inventory</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <KindIcon kind={item.kind} size={22} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="kicker font-mono">{item.sku}</span>
              <KindBadge kind={item.kind} />
              {item.fscCertified && <Badge tone="success"><Leaf size={10} />&nbsp;{item.fscClaim ?? 'FSC®'}</Badge>}
              {out && <Badge tone="danger">Out of Stock</Badge>}
              {low && <Badge tone="warning">Low Stock</Badge>}
              {!item.active && <Badge tone="neutral">Inactive</Badge>}
            </div>
            <h1 className="font-display text-3xl text-text leading-tight tracking-tight">{item.name}</h1>
            {item.description && (
              <p className="text-text-muted mt-2 text-sm max-w-2xl leading-relaxed">{item.description}</p>
            )}
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            <Button leftIcon={<Plus size={13} />} onClick={() => openMove('receipt')}>
              Record Movement
            </Button>
            <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Deactivate">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </header>

      {/* Stock summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <BigStat
          label="Total On Hand"
          value={formatNumber(item.totalOnHand ?? 0)}
          suffix={item.unit}
          accent
        />
        <BigStat
          label="Unit Cost"
          value={item.unitCost !== undefined ? formatCurrency(item.unitCost) : '—'}
          suffix={item.unitCost !== undefined ? `per ${item.unit}` : ''}
        />
        <BigStat
          label="Stock Value"
          value={item.valueAtCost !== undefined ? formatCurrency(item.valueAtCost) : '—'}
        />
        <BigStat
          label="Reorder Point"
          value={item.reorderPoint !== undefined ? formatNumber(item.reorderPoint) : '—'}
          suffix={item.reorderPoint !== undefined ? item.unit : ''}
          alert={low || out}
        />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Left — by location & movements */}
        <div className="space-y-6">
          {/* By location */}
          <Card>
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="kicker mb-1">Stock by Location</div>
                <div className="text-xs text-text-muted">
                  Zero-quantity locations hidden. Use Record Movement → Transfer to redistribute.
                </div>
              </div>
              {canWrite && (
                <Button size="sm" variant="secondary" leftIcon={<ArrowRight size={13} />} onClick={() => openMove('transfer')}>
                  Transfer
                </Button>
              )}
            </div>

            <LocationBreakdown onHand={item.onHand ?? {} as Record<Location, number>} unit={item.unit} />
          </Card>

          {/* Ledger */}
          <Card className="p-0 overflow-hidden">
            <header className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <div className="kicker mb-1">Movement Ledger · {movements?.length ?? 0}</div>
                <h3 className="font-display text-lg text-text">Activity</h3>
              </div>
              {canWrite && movements && movements.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => openMove('receipt')}>
                  + New
                </Button>
              )}
            </header>

            {!movements || movements.length === 0 ? (
              <EmptyState
                icon={Package}
                title="No activity yet"
                description="Record a receipt, adjustment, or consumption to start the ledger."
                action={canWrite && (
                  <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => openMove('receipt')}>
                    Record Movement
                  </Button>
                )}
              />
            ) : (
              <ul className="divide-y divide-border">
                {movements.map((m) => (
                  <MovementRow key={m.id} movement={m} unit={item.unit} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Right — sidebar details */}
        <div className="space-y-4">
          {/* Veneer spec card */}
          {item.kind === 'veneer' && (item.veneerSpecies || item.veneerCut || item.flitchNumber) && (
            <Card>
              <div className="kicker mb-3 flex items-center gap-2">
                <Leaf size={11} /> Veneer Spec
              </div>
              <dl className="space-y-2.5 text-sm">
                {item.veneerSpecies && <DetailRow label="Species" value={item.veneerSpecies} />}
                {item.flitchNumber && <DetailRow label="Flitch #" value={item.flitchNumber} mono />}
                {item.veneerCut && <DetailRow label="Cut" value={VENEER_CUT_LABELS[item.veneerCut]} />}
                {item.veneerFigure && <DetailRow label="Figure" value={VENEER_FIGURE_LABELS[item.veneerFigure]} />}
                {item.veneerMatch && <DetailRow label="Match" value={VENEER_MATCH_LABELS[item.veneerMatch]} />}
                {item.sqFtPerBundle && <DetailRow label="Sq Ft / Bundle" value={formatNumber(item.sqFtPerBundle)} />}
              </dl>
            </Card>
          )}

          {/* Dimensions */}
          {(item.dimensions || item.thickness || item.grade) && item.kind !== 'veneer' && (
            <Card>
              <div className="kicker mb-3">Specifications</div>
              <dl className="space-y-2.5 text-sm">
                {item.dimensions && <DetailRow label="Dimensions" value={item.dimensions} />}
                {item.thickness && <DetailRow label="Thickness" value={item.thickness} />}
                {item.grade && <DetailRow label="Grade" value={item.grade} />}
              </dl>
            </Card>
          )}

          {/* Reorder / supplier */}
          <Card>
            <div className="kicker mb-3">Reorder Policy</div>
            <dl className="space-y-2.5 text-sm">
              {item.reorderPoint !== undefined && (
                <DetailRow
                  label="Reorder at"
                  value={`${formatNumber(item.reorderPoint)} ${item.unit}`}
                />
              )}
              {item.reorderQty !== undefined && (
                <DetailRow
                  label="Reorder qty"
                  value={`${formatNumber(item.reorderQty)} ${item.unit}`}
                />
              )}
              {item.preferredSupplier && (
                <DetailRow label="Supplier" value={item.preferredSupplier} />
              )}
              {!item.reorderPoint && !item.preferredSupplier && (
                <p className="text-xs text-text-dim">No reorder policy set.</p>
              )}
            </dl>
          </Card>

          {/* Quick actions */}
          {canWrite && (
            <Card>
              <div className="kicker mb-3">Quick Actions</div>
              <div className="grid grid-cols-2 gap-2">
                <QuickAction icon={Truck}           label="Receipt"     onClick={() => openMove('receipt')} />
                <QuickAction icon={Wrench}          label="Consume"     onClick={() => openMove('consumption')} />
                <QuickAction icon={ArrowRight}      label="Transfer"    onClick={() => openMove('transfer')} />
                <QuickAction icon={ClipboardCheck}  label="Adjust"      onClick={() => openMove('adjustment')} />
                <QuickAction icon={Undo2}           label="Return"      onClick={() => openMove('return')} />
                <QuickAction icon={AlertTriangle}  label="Scrap"       onClick={() => openMove('scrap')} danger />
              </div>
            </Card>
          )}
        </div>
      </div>

      <ItemFormModal open={editOpen} onClose={() => setEditOpen(false)} item={item} />
      <RecordMovementModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        item={item}
        defaultType={moveType}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        loading={deleteItem.isPending}
        destructive
        title={`Deactivate ${item.sku}?`}
        confirmLabel="Deactivate"
        description="This soft-deletes the item. Stock history is preserved."
      />
    </div>
  );
}

function LocationBreakdown({ onHand, unit }: { onHand: Record<Location, number>; unit: string }) {
  const entries = Object.entries(onHand)
    .filter(([, qty]) => qty !== 0)
    .sort(([, a], [, b]) => b - a);

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-text-dim border border-dashed border-border rounded-sm">
        No stock in any location.
      </div>
    );
  }

  const total = entries.reduce((s, [, q]) => s + q, 0);

  return (
    <div className="space-y-3">
      {entries.map(([loc, qty]) => {
        const pct = total > 0 ? (qty / total) * 100 : 0;
        return (
          <div key={loc}>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-sm text-text">{LOCATION_LABELS[loc as Location]}</span>
              <span className="text-sm tabular text-text">
                {formatNumber(qty)}{' '}
                <span className="text-xs text-text-dim">{unit}</span>
              </span>
            </div>
            <div className="h-1 bg-bg-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-brass-400 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MovementRow({ movement: m, unit }: { movement: StockMovement; unit: string }) {
  const Icon = MOVEMENT_ICONS[m.type];
  const isPositive = m.quantity > 0;
  const isTransfer = m.type === 'transfer';

  return (
    <li className="px-6 py-4 hover:bg-bg-hover/30 transition-colors">
      <div className="flex items-start gap-4">
        <div className={`
          shrink-0 h-9 w-9 rounded-sm flex items-center justify-center
          ${isPositive
            ? 'bg-success/10 border border-success/30'
            : 'bg-bg-subtle border border-border'}
        `}>
          <Icon size={14} className={isPositive ? 'text-success' : 'text-text-muted'} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-sm text-text">{MOVEMENT_TYPE_LABELS[m.type]}</span>
            <span className={`text-sm tabular font-medium ${isPositive ? 'text-success' : 'text-text-muted'}`}>
              {isPositive ? '+' : ''}{formatNumber(m.quantity)} {unit}
            </span>
            {isTransfer && m.toLocation && (
              <span className="text-xs text-text-dim inline-flex items-center gap-1.5">
                {LOCATION_LABELS[m.location]}
                <ArrowRight size={10} />
                {LOCATION_LABELS[m.toLocation]}
              </span>
            )}
            {!isTransfer && (
              <span className="text-xs text-text-dim">{LOCATION_LABELS[m.location]}</span>
            )}
          </div>

          <div className="mt-1 flex items-center gap-2 text-2xs text-text-dim tabular">
            {new Date(m.occurredAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            {m.authorName && <><span className="text-border-strong">·</span><span>{m.authorName}</span></>}
            {m.orderNumber && <><span className="text-border-strong">·</span><span className="font-mono text-brass-300">{m.orderNumber}</span></>}
            {m.poNumber && <><span className="text-border-strong">·</span><span className="font-mono">PO {m.poNumber}</span></>}
            {m.batch && <><span className="text-border-strong">·</span><span className="font-mono">Flitch {m.batch}</span></>}
          </div>

          {(m.notes || m.reason) && (
            <p className="mt-2 text-xs text-text-muted leading-relaxed">
              {m.reason && <span className="text-text">{m.reason}. </span>}
              {m.notes}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-text-dim uppercase tracking-ultra">{label}</dt>
      <dd className={`text-text ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

function BigStat({
  label, value, suffix, accent, alert,
}: {
  label: string; value: string; suffix?: string;
  accent?: boolean; alert?: boolean;
}) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
          {value}
        </div>
        {suffix && <span className="text-xs text-text-dim">{suffix}</span>}
      </div>
    </div>
  );
}

function QuickAction({
  icon: Icon, label, onClick, danger,
}: {
  icon: typeof Truck; label: string; onClick: () => void; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex flex-col items-center gap-1.5 p-3 rounded-sm border transition-colors text-xs uppercase tracking-[0.15em]
        ${danger
          ? 'border-border text-text-muted hover:text-danger hover:border-danger/40 hover:bg-danger/5'
          : 'border-border text-text-muted hover:text-text hover:border-border-strong hover:bg-bg-hover'}
      `}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}
